import React, { useEffect, useState } from "react";
import { ShieldAlert, CalendarClock, Target, MessageSquare } from "lucide-react";

// Staff-facing, READ-ONLY view of the caller's own Performance Improvement
// Plan. Staff have no write path (per the PIP contract). The component
// self-hides (renders null) when the caller has no active/extended PIP — most
// staff — so it adds nothing to the page unless a plan actually applies.

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

// Status vocab is lowercase and independent of `outcome` (improved→completed,
// exited→terminated) — render status directly, never derive it from outcome.
const STATUS_STYLE = {
  draft:      "text-white/50 bg-white/5 border-white/15",
  active:     "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/25",
  extended:   "text-amber-400 bg-amber-500/10 border-amber-500/25",
  completed:  "text-blue-400 bg-blue-500/10 border-blue-500/20",
  terminated: "text-red-400 bg-red-500/10 border-red-500/20",
};
const STATUS_FALLBACK = "text-white/30 bg-white/5 border-white/10";
const STATUS_LABEL = {
  draft: "Draft", active: "Active", extended: "Extended",
  completed: "Completed", terminated: "Terminated",
};

const RATING_STYLE = {
  on_track:          "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/25",
  needs_improvement: "text-amber-400 bg-amber-500/10 border-amber-500/25",
  not_meeting:       "text-red-400 bg-red-500/10 border-red-500/20",
};
const RATING_LABEL = {
  on_track: "On Track", needs_improvement: "Needs Improvement", not_meeting: "Not Meeting",
};

const badge = (map, fallback, key, label) => (
  <span className={`text-xs px-2 py-0.5 rounded-full border ${map[key] || fallback}`}>
    {label || key || "—"}
  </span>
);

const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");

export default function MyPIPCard() {
  const [pip, setPip]         = useState(undefined); // undefined=loading, null=none
  const [checkins, setChecks] = useState([]);
  const token = localStorage.getItem("token");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/performance/pip/mine`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.ok ? await res.json() : null;
        if (!active) return;
        setPip(data || null);
        if (data?.id) {
          const cr = await fetch(`${API_BASE}/api/performance/pip/${data.id}/checkins`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const list = cr.ok ? await cr.json() : [];
          if (active) setChecks(Array.isArray(list) ? list : []);
        }
      } catch {
        if (active) setPip(null);
      }
    })();
    return () => { active = false; };
  }, [token]);

  // Loading or no PIP → render nothing (quiet empty state; never an error box).
  if (!pip) return null;

  return (
    <div className={`${card} p-5 space-y-4`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <ShieldAlert size={16} className="text-amber-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Improvement Plan</p>
            <p className="text-sm text-white/80 font-medium truncate">{pip.title}</p>
          </div>
        </div>
        {badge(STATUS_STYLE, STATUS_FALLBACK, pip.status, STATUS_LABEL[pip.status])}
      </div>

      {pip.reason && (
        <div>
          <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-1">Reason</p>
          <p className="text-sm text-white/60 whitespace-pre-wrap">{pip.reason}</p>
        </div>
      )}
      {pip.goals && (
        <div>
          <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-1 flex items-center gap-1">
            <Target size={11} /> Goals
          </p>
          <p className="text-sm text-white/60 whitespace-pre-wrap">{pip.goals}</p>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        {[["Start", pip.start_date], ["Review", pip.review_date], ["End", pip.end_date]].map(([l, d]) => (
          <div key={l} className="rounded-lg border border-white/5 bg-white/3 p-2.5">
            <p className="text-white/30 mb-0.5">{l}</p>
            <p className="text-white/70">{fmtDate(d)}</p>
          </div>
        ))}
        <div className="rounded-lg border border-white/5 bg-white/3 p-2.5">
          <p className="text-white/30 mb-0.5 flex items-center gap-1"><CalendarClock size={10} /> Check-ins</p>
          <p className="text-white/70 capitalize">{pip.check_in_frequency || "—"}</p>
        </div>
      </div>

      {/* Check-in timeline — newest-first (matches the API order; most-recent
          feedback is what a staff member wants to see first). */}
      <div className="border-t border-white/5 pt-3">
        <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-2 flex items-center gap-1">
          <MessageSquare size={11} /> Check-in History
        </p>
        {checkins.length === 0 ? (
          <p className="text-xs text-white/25 italic">No check-ins recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {checkins.map((c) => (
              <div key={c.id} className="rounded-lg border border-white/5 bg-white/3 p-3 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  {badge(RATING_STYLE, STATUS_FALLBACK, c.rating, RATING_LABEL[c.rating])}
                  <span className="text-xs text-white/25">{fmtDate(c.completed_at || c.created_at)}</span>
                </div>
                {c.manager_notes && (
                  <p className="text-xs text-white/60"><span className="text-white/30">Manager: </span>{c.manager_notes}</p>
                )}
                {c.employee_response && (
                  <p className="text-xs text-white/60"><span className="text-white/30">Your response: </span>{c.employee_response}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
